import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AgnesAutocomplete,
  AgnesButton,
  AgnesCard,
  AgnesCardContent,
  AgnesCardHeader,
  AgnesDataTable,
  AgnesDatePickerInput,
  AgnesLabel,
  AgnesPageHeader,
  AgnesPopover,
  AgnesPopoverContent,
  AgnesPopoverTrigger,
  AgnesSelect,
  AgnesSelectContent,
  AgnesSelectItem,
  AgnesSelectTrigger,
  AgnesSelectValue,
} from '@agnes/ui';
import { Copy, Eye, Filter, History, Plus, RefreshCw, Trash2, X } from 'lucide-react';
import { useCentralReprocessamentoStore } from '@/stores/useCentralReprocessamentoStore';
import { usePermissionsStore } from '@/stores/usePermissionsStore';
import MENU_PERMISSIONS from '@/constants/ConstantMenuRoles';
import { type RootCnpjItem, useCompaniesStore } from '@/stores/useCompaniesStore';
import { countFiltros, labelFiltro } from '@/lib/filtros';
import { confirm } from '@/stores/useConfirm';
import { SearchInput } from '@/components/SearchInput';
import { BadgeCentralReprocessamentoStatus } from '@/components/central-reprocessamento/BadgeCentralReprocessamentoStatus';
import { CentralReprocessamentoFormularioDialog } from '@/components/central-reprocessamento/CentralReprocessamentoFormularioDialog';
import { CENTRAL_REPROCESSAMENTO_TIPOS_ENUM } from '@/constants/central-reprocessamento/ConstantCentralReprocessamentoTipos';
import { CENTRAL_REPROCESSAMENTO_STATUS_ENUM } from '@/constants/central-reprocessamento/ConstantCentralReprocessamentoStatus';
import UtilsMask from '@/utils/UtilsMask';
import UtilsDate from '@/utils/UtilsDate';
import { TableWrapper } from '@/components/table-wrapper/TableWrapper';

const POLL_MS = Number(import.meta.env.VITE_APP_INTERVALO_REQUISICOES_MS || 300000);

const TIPO_ITEMS = CENTRAL_REPROCESSAMENTO_TIPOS_ENUM.asItems();
const STATUS_ITEMS = CENTRAL_REPROCESSAMENTO_STATUS_ENUM.asItems();

type Row = any;

// popover Filtro (Tipo / Status / Empresa) — mesmo padrão de empresas por raiz CNPJ
// já usado no CentralReprocessamentoFormularioDialog (useCompaniesStore.fetchRootCnpjs)
function FiltroPopover() {
  const rootCnpjs = useCompaniesStore((s) => s.rootCnpjs);
  const [open, setOpen] = React.useState(false);
  const [tipo, setTipo] = React.useState('');
  const [status, setStatus] = React.useState('');
  const [empresas, setEmpresas] = React.useState<RootCnpjItem[]>([]);
  const [dataInicio, setDataInicio] = React.useState('');
  const [dataFim, setDataFim] = React.useState('');
  const filtrosAplicados = useCentralReprocessamentoStore((s) => s.filtros);
  const nFiltros = countFiltros(filtrosAplicados, {
    ignore: ['busca'],
    dateRanges: [['dataInicio', 'dataFim']],
  });

  React.useEffect(() => {
    if (!open) return;
    useCompaniesStore.getState().fetchRootCnpjs();
  }, [open]);

  function toggleEmpresa(opt: RootCnpjItem) {
    setEmpresas((prev) =>
      prev.some((e) => e.value === opt.value)
        ? prev.filter((e) => e.value !== opt.value)
        : [...prev, opt],
    );
  }

  function aplicarLimpar(f: {
    tipo: string;
    status: string;
    empresas: RootCnpjItem[];
    dataInicio: string;
    dataFim: string;
  }) {
    setOpen(false);
    setTipo(f.tipo);
    setStatus(f.status);
    setEmpresas(f.empresas);
    setDataInicio(f.dataInicio);
    setDataFim(f.dataFim);
    const { paginacaoListaReprocessamentos, filtros } = useCentralReprocessamentoStore.getState();
    useCentralReprocessamentoStore.getState().setFiltros({
      tipo: f.tipo || null,
      status: f.status || null,
      nrinsc: f.empresas.map((e) => e.value),
      busca: filtros.busca, // preserva a busca livre — este popover não mexe nela
      dataInicio: f.dataInicio || null,
      dataFim: f.dataFim || null,
    });
    useCentralReprocessamentoStore
      .getState()
      .setPaginacao({ ...paginacaoListaReprocessamentos, page: 1 });
    useCentralReprocessamentoStore.getState().pesquisar(false);
  }

  return (
    <AgnesPopover open={open} onOpenChange={setOpen}>
      <AgnesPopoverTrigger asChild>
        <AgnesButton
          variant="outlined"
          startIcon={<Filter className="h-4 w-4" />}
          aria-label={nFiltros > 0 ? `Filtro, ${nFiltros} ativos` : 'Filtro'}
        >
          {labelFiltro('Filtro', nFiltros)}
        </AgnesButton>
      </AgnesPopoverTrigger>
      <AgnesPopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between border-b border-agnes-border px-4 py-3">
          <span className="text-base font-semibold text-agnes-text">Filtro</span>
          <button
            type="button"
            aria-label="Fechar"
            onClick={() => setOpen(false)}
            className="text-agnes-text-muted hover:text-agnes-text"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="flex flex-col gap-3 px-4 py-4">
          <div>
            <AgnesLabel>Tipo</AgnesLabel>
            <AgnesSelect value={tipo} onValueChange={setTipo}>
              <AgnesSelectTrigger>
                <AgnesSelectValue placeholder="Selecione o tipo" />
              </AgnesSelectTrigger>
              <AgnesSelectContent position="popper">
                {TIPO_ITEMS.map((i: { value: string; title: string }) => (
                  <AgnesSelectItem key={i.value} value={i.value}>
                    {i.title}
                  </AgnesSelectItem>
                ))}
              </AgnesSelectContent>
            </AgnesSelect>
          </div>
          <div>
            <AgnesLabel>Status</AgnesLabel>
            <AgnesSelect value={status} onValueChange={setStatus}>
              <AgnesSelectTrigger>
                <AgnesSelectValue placeholder="Selecione o status" />
              </AgnesSelectTrigger>
              <AgnesSelectContent
                align="center"
                sideOffset={4}
                className="w-[var(--radix-select-trigger-width)] min-w-0"
              >
                {STATUS_ITEMS.map((i: { value: string; title: string }) => (
                  <AgnesSelectItem key={i.value} value={i.value}>
                    {i.title}
                  </AgnesSelectItem>
                ))}
              </AgnesSelectContent>
            </AgnesSelect>
          </div>
          <div>
            <AgnesLabel>Empresa</AgnesLabel>
            <AgnesAutocomplete<RootCnpjItem>
              options={rootCnpjs}
              value={empresas}
              onOptionToggle={toggleEmpresa}
              getOptionLabel={(o) => `${o.title} - ${o.subtitle}`}
              isOptionEqualToValue={(a, b) => a.value === b.value}
              placeholder="Selecione a empresa"
              noOptionsText="Nenhuma empresa"
            />
          </div>
          <div>
            <AgnesLabel>Período de processamento</AgnesLabel>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <AgnesDatePickerInput
                  value={dataInicio}
                  onChange={(value) => setDataInicio(value)}
                  aria-label="De"
                  dataQa="filtro-periodo-de"
                />
              </div>
              <div>
                <AgnesDatePickerInput
                  value={dataFim}
                  onChange={(value) => setDataFim(value)}
                  aria-label="Até"
                  dataQa="filtro-periodo-ate"
                />
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-2 border-t border-agnes-border px-4 py-3">
          <AgnesButton
            variant="primary"
            onClick={() => aplicarLimpar({ tipo, status, empresas, dataInicio, dataFim })}
            className="w-full"
          >
            Aplicar filtro
          </AgnesButton>

          <AgnesButton
            variant="outlined"
            onClick={() =>
              aplicarLimpar({ tipo: '', status: '', empresas: [], dataInicio: '', dataFim: '' })
            }
            startIcon={<Trash2 className="h-4 w-4" />}
            className="w-full"
          >
            Limpar filtro
          </AgnesButton>
        </div>
      </AgnesPopoverContent>
    </AgnesPopover>
  );
}

export default function PageCentralReprocessamento() {
  const navigate = useNavigate();
  const lista = useCentralReprocessamentoStore((s) => s.listaReprocessamentos);
  const total = useCentralReprocessamentoStore((s) => s.totalReprocessamentos);
  const loading = useCentralReprocessamentoStore((s) => s.estaPesquisandoReprocessamentos);
  const paginacao = useCentralReprocessamentoStore((s) => s.paginacaoListaReprocessamentos);
  // Só quem tem a permissão da Central vê o botão de histórico e navega para a tela (CON-5281).
  const podeHistorico = usePermissionsStore((s) =>
    s.hasPermission(MENU_PERMISSIONS.CENTRAL_REPROCESSAMENTO),
  );

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [dialogId, setDialogId] = React.useState<number | undefined>(undefined);
  const [busca, setBusca] = React.useState('');

  function onBuscar() {
    const { filtros, paginacaoListaReprocessamentos } = useCentralReprocessamentoStore.getState();
    useCentralReprocessamentoStore.getState().setFiltros({ ...filtros, busca });
    useCentralReprocessamentoStore
      .getState()
      .setPaginacao({ ...paginacaoListaReprocessamentos, page: 1 });
    useCentralReprocessamentoStore.getState().pesquisar(false);
  }

  React.useEffect(() => {
    useCentralReprocessamentoStore.getState().pesquisar(false);
    const t = setInterval(() => useCentralReprocessamentoStore.getState().pesquisar(true), POLL_MS);
    return () => clearInterval(t);
  }, []);

  async function onDuplicar(row: Row) {
    const nome = useCentralReprocessamentoStore.getState().getNomeCompletoReprocessamento(row);
    const ok = await confirm({
      message: `Você tem certeza de que deseja duplicar o reprocessamento?<br/><b>${nome}</b>`,
      okLabel: 'Sim, duplicar',
      cancelLabel: 'Não, cancelar',
    });
    if (!ok) return;
    await useCentralReprocessamentoStore.getState().duplicar(row);
    useCentralReprocessamentoStore.getState().pesquisar();
  }

  const columns = [
    { key: 'id', header: 'ID', width: 56, sortable: true },
    {
      key: 'tipo',
      header: 'Tipo',
      width: 200,
      render: (r: Row) => CENTRAL_REPROCESSAMENTO_TIPOS_ENUM.get(r.tipo).getDescription(),
    },
    {
      key: 'razaoSocial',
      header: 'Razão Social',
      width: 300,
      render: (r: Row) => r.razaoSocial || <span className="text-agnes-text-muted">-</span>,
    },
    {
      key: 'nrinsc',
      header: 'Raíz CNPJ',
      width: 200,
      render: (r: Row) => UtilsMask.cnpj(r.nrinsc),
    },
    {
      key: 'status',
      header: 'Status',
      width: 200,
      render: (r: Row) => (
        <>
          <BadgeCentralReprocessamentoStatus status={r.status} />
        </>
      ),
    },
    {
      key: 'duracao',
      header: 'Duração',
      width: 150,
      render: (r: Row) => UtilsDate.duration(r.iniciadoEm, r.finalizadoEm || new Date()),
    },
    {
      key: 'atualizadoEm',
      header: 'Última atualização em',
      width: 300,
      sortable: true,
      render: (r: Row) =>
        UtilsDate.format2(r.atualizadoEm, { toFormat: 'DD/MM/YYYY [às] HH:mm:ss' } as any),
    },
  ];

  function actions(r: Row) {
    const podeResolicitar = useCentralReprocessamentoStore
      .getState()
      .verificarPermissaoSolicitacao(r);
    return (
      <div className="flex items-center gap-1">
        <AgnesButton
          variant="ghost"
          size="sm"
          disabled={!podeResolicitar}
          title="Resolicitar"
          onClick={() => {
            setDialogId(r.id);
            setDialogOpen(true);
          }}
        >
          <RefreshCw className="h-4 w-4" />
        </AgnesButton>
        <AgnesButton
          variant="ghost"
          size="sm"
          title="Visualizar"
          onClick={() => navigate(`/central-de-reprocessamento/${r.id}`)}
        >
          <Eye className="h-4 w-4" />
        </AgnesButton>
        {podeHistorico && (
          <AgnesButton
            variant="ghost"
            size="sm"
            title="Histórico"
            aria-label="Histórico"
            data-qa="reprocessamento-acao-historico"
            onClick={() => navigate(`/central-de-reprocessamento/historico/${r.nrinsc}`)}
          >
            <History className="h-4 w-4" />
          </AgnesButton>
        )}
        <AgnesButton variant="ghost" size="sm" title="Duplicar" onClick={() => onDuplicar(r)}>
          <Copy className="h-4 w-4" />
        </AgnesButton>
      </div>
    );
  }

  function onPageChange(zeroBasedPage: number) {
    useCentralReprocessamentoStore
      .getState()
      .setPaginacao({ ...paginacao, page: zeroBasedPage + 1 });
    useCentralReprocessamentoStore.getState().pesquisar(false);
  }

  function onSortChange(sortBy: string, sortOrder: 'asc' | 'desc') {
    useCentralReprocessamentoStore
      .getState()
      .setPaginacao({ ...paginacao, page: 1, sortBy: [{ key: sortBy, order: sortOrder }] });
    useCentralReprocessamentoStore.getState().pesquisar(false);
  }

  return (
    <>
      <AgnesPageHeader
        icon={<RefreshCw />}
        title="Central de Reprocessamento"
        subtitle="Página para criar e gerenciar reprocessamentos de dados do eSocial"
        breadcrumbs={[
          { label: 'Home', href: '/home' },
          { label: 'Administração' },
          { label: 'eSocial' },
          { label: 'Central de Reprocessamento' },
        ]}
        actions={
          <AgnesButton
            variant="primary"
            startIcon={<Plus className="h-4 w-4" />}
            onClick={() => {
              setDialogId(undefined);
              setDialogOpen(true);
            }}
          >
            Novo reprocessamento
          </AgnesButton>
        }
      />

      <AgnesCard>
        <AgnesCardHeader>
          <div className="min-w-[320px] flex-1">
            <SearchInput
              value={busca}
              onValueChange={setBusca}
              onSearch={onBuscar}
              placeholder="Pesquisar por Razão social ou CNPJ raiz..."
            />
          </div>
          <FiltroPopover />
        </AgnesCardHeader>

        <AgnesCardContent className="flex items-stretch">
          <TableWrapper>
            <AgnesDataTable<Row>
              columns={columns}
              data={lista}
              rowKey={(r) => String(r.id)}
              actions={actions}
              loading={loading}
              sort={{
                sortBy: paginacao.sortBy?.[0]?.key ?? '',
                sortOrder: paginacao.sortBy?.[0]?.order ?? 'desc',
                onSortChange,
              }}
              pagination={{
                page: (paginacao.page ?? 1) - 1,
                pageSize: paginacao.itemsPerPage,
                total,
                onPageChange,
                serverSide: true,
              }}
            />
          </TableWrapper>
        </AgnesCardContent>
      </AgnesCard>

      <CentralReprocessamentoFormularioDialog
        open={dialogOpen}
        id={dialogId}
        onClose={() => setDialogOpen(false)}
        onConfirm={() => {
          setDialogOpen(false);
          useCentralReprocessamentoStore.getState().pesquisar();
        }}
      />
    </>
  );
}
